// After an outbound call (CU-869fmbgfn): ask only when we don't know.
// Backend V2-5 (contract ~/pm/contracts/V2-5.md v1.1) decides per call:
//   auto_logged   → toast "{label}" with Undo (rang out / busy / voicemail)
//   needs_outcome → the one question, from `options`
//   anything else → nothing (already logged, in progress, inbound, too old)
// Pure helpers + the three calls; no React, so Jest loads them on their own.
import { callApi } from '../../utils/apiCall';
import { CALL_STATE } from './callPillModel';

export const OUTCOME_STATE = Object.freeze({
    AUTO_LOGGED: 'auto_logged',
    LOGGED: 'logged',
    NEEDS_OUTCOME: 'needs_outcome',
    IN_PROGRESS: 'in_progress',
    NONE: 'none',
});

export const SPOKE_NEW_LEAD = 'spoke_new_lead';

// B2's call_id is the voice call's id; V2-5 addresses Call Logs rows.
export const outcomeRef = (callId) => (String(callId).includes(':') ? String(callId) : `voice:${callId}`);
const base = (practiceId, callId) => `/api/v1/practices/${practiceId}/calls/${encodeURIComponent(outcomeRef(callId))}/call-outcome/`;
export const OUTCOME_URLS = {
    get: base,
    log: base,
    undo: (practiceId, callId) => `${base(practiceId, callId)}undo/`,
};

// Writes: a longer timeout and no global "Something went wrong" (status 0 or a
// 5xx may still have been saved; GOTCHAS: callApi status 0 is not "failed").
const WRITE_OPTS = { timeout: 20000, suppressServerErrorDispatch: true };

export const fetchCallOutcome = (practiceId, callId) => callApi(OUTCOME_URLS.get(practiceId, callId), 'GET', null, false, { suppressServerErrorDispatch: true });
export const logCallOutcome = (practiceId, callId, outcome, idempotencyKey) => callApi(
    OUTCOME_URLS.log(practiceId, callId), 'POST', { outcome, label: '' }, false,
    { ...WRITE_OPTS, headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {} },
);
export const undoCallOutcome = (practiceId, callId) => callApi(OUTCOME_URLS.undo(practiceId, callId), 'POST', {}, false, WRITE_OPTS);

const STATES = new Set(Object.values(OUTCOME_STATE));

// The outcome object, or null when the backend has nothing usable (old backend
// without V2-5 → 404, no answer, an unknown state).
export const readOutcome = (response) => {
    const data = response?.ok ? response.data : null;
    if (!data || !STATES.has(data.state)) return null;
    return {
        state: data.state,
        label: typeof data.label === 'string' ? data.label : '',
        options: Array.isArray(data.options) ? data.options.filter((o) => o && o.id && o.label) : [],
        undoAllowed: Boolean(data.undo?.allowed),
        couldntReach: Boolean(data.couldnt_reach),
    };
};

// Which ended calls this browser checks: this user's own outbound call
// (never a DentalSEM staff observer copy).
export const wantsOutcomeCheck = (call) => Boolean(
    call && call.state === CALL_STATE.ENDED && call.mine && call.direction === 'outbound'
    && call.recipientReason !== 'superuser' && call.callId != null,
);

// What a write's answer means for the UI. No answer or a 5xx: it may have been
// saved, so never say it failed and never invite a blind retry.
export const WRITE = Object.freeze({ OK: 'ok', UNKNOWN: 'unknown', ERROR: 'error' });
export const readWrite = (response, fallback) => {
    if (!response || response.status === 0 || response.status >= 500) return { kind: WRITE.UNKNOWN };
    if (response.ok) return { kind: WRITE.OK, outcome: readOutcome(response) };
    const data = response.data || {};
    if (data.code === 'undo_expired') return { kind: WRITE.ERROR, message: 'Too late to undo (7 days have passed).' };
    if (data.code === 'nothing_to_undo') return { kind: WRITE.ERROR, message: 'Nothing to undo: it was already changed.' };
    if (response.status === 403) return { kind: WRITE.ERROR, message: 'You can\'t record an outcome for this call.' };
    let detail = Array.isArray(data.outcome) ? data.outcome[0] : data.outcome;
    if (typeof data.detail === 'string') detail = data.detail;
    return { kind: WRITE.ERROR, message: (typeof detail === 'string' && detail) || fallback };
};

export const COULDNT_CONFIRM = 'Couldn\'t confirm it was saved. Check Call Logs before trying again.';

// One tab handles each call's outcome (several CRM tabs all get the ended
// event): the first to claim it in localStorage wins; background tabs wait a
// moment so the tab in front claims first.
export const CLAIM_TTL_MS = 10 * 60 * 1000;
export const BACKGROUND_TAB_DELAY_MS = 1500;
export const claimOutcome = (practiceId, callId, now = Date.now()) => {
    try {
        const key = `call-outcome:${practiceId}:${callId}`;
        const prev = Number(window.localStorage.getItem(key) || 0);
        if (prev && now - prev < CLAIM_TTL_MS) return false;
        window.localStorage.setItem(key, String(now));
        return true;
    } catch {
        return true;   // no storage: this tab handles it
    }
};

// "Not a patient" needs Call Logs manage and an unknown caller (contract v1.1):
// don't offer what the server would refuse.
export const NOT_A_PATIENT = 'not_a_patient';
export const optionsFor = (options, { canManageCallLogs = false, knownCaller = false } = {}) => (options || [])
    .filter((o) => o.id !== NOT_A_PATIENT || (canManageCallLogs && !knownCaller));
